import { Link } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  InvoiceTransactionLinkStatusIcon,
  getLinkStatusForOneTransaction,
} from "@/components/shared/custom-icons/invoice-transaction-link-status-icon";
import { Card } from "@/components/ui/card";
import { TypographyLarge } from "@/components/ui/typography";
import { TransactionCard } from "@/features/transactions/shared/transactions-todos/cells/transaction-cell";
import useReconEnabled from "@/hooks/use-recon-enabled";
import { useTenant } from "@/providers/session";
import type { InvoiceDetailsTransaction } from "@/queries/invoices/get";

type Props = {
  transactions: InvoiceDetailsTransaction[];
  reconTriggered?: boolean;
};

export default function InvoiceBankReconciliation({
  transactions,
  reconTriggered,
}: Props) {
  const { companySlug } = useTenant();
  const { t } = useTranslation();
  const { hasBankConnections, reconEnabled } = useReconEnabled();

  if (!hasBankConnections) return null;

  return (
    <div>
      <TypographyLarge className="mb-2">
        {t("bank-reconciliation")}
      </TypographyLarge>
      {transactions.length === 0 && reconEnabled && reconTriggered === false ? (
        <Card className="px-3 py-3">
          <div className="flex flex-row items-center gap-3">
            <InvoiceTransactionLinkStatusIcon linkStatus="ai-in-progress" />
            <div className="flex flex-col">
              <p className="font-medium">
                {t("invoice-bank-reconciliation.ai_in_progress_title")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("invoice-bank-reconciliation.ai_in_progress_description")}
              </p>
            </div>
          </div>
        </Card>
      ) : transactions.length === 0 ? (
        <Card className="px-3 py-3">
          <div className="flex flex-row items-center gap-3">
            <InvoiceTransactionLinkStatusIcon linkStatus="unattached" />
            <div className="flex flex-col">
              <p className="font-medium">
                {t("invoice-bank-reconciliation.unlinked_title")}
              </p>
              <p className="text-sm text-muted-foreground">
                {t("invoice-bank-reconciliation.unlinked_description")}
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {transactions.map((transaction) => {
            const linkStatus = getLinkStatusForOneTransaction(transaction);
            const transactionLink =
              linkStatus === "attached"
                ? `/${companySlug}/unternehmen/transaktionen`
                : `/${companySlug}/unternehmen/transaktionen/todos`;

            return (
              <Card className="px-2" key={transaction.bankTxnId}>
                <div className="flex flex-col">
                  <TransactionCard
                    transaction={transaction}
                    icon={
                      <InvoiceTransactionLinkStatusIcon
                        linkStatus={linkStatus}
                      />
                    }
                  />
                  <div className="flex justify-end gap-2">
                    <Link
                      to={
                        linkStatus === "attached"
                          ? "/$company/unternehmen/transaktionen"
                          : "/$company/unternehmen/transaktionen/todos"
                      }
                      params={{ company: companySlug }}
                      search={
                        { global_search: transaction.purpose || "" } as any
                      }
                      className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1 px-2 pb-2"
                    >
                      <span>
                        {t("invoice-bank-reconciliation.search_transaction")}
                      </span>
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
